import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  HouseSimple, Package, Tag, Article, ChatCircleText, Briefcase, Files,
  UsersThree, ImageSquare, Stack, IdentificationCard, Gear, MagnifyingGlass,
  UserCircle, SignOut, Buildings, ArrowsLeftRight, Question, VideoCamera,
} from "@phosphor-icons/react";

const navItems = [
  { to: "/admin", label: "Dashboard", icon: HouseSimple, end: true },
  { to: "/admin/products", label: "Products", icon: Package },
  { to: "/admin/categories", label: "Categories", icon: Tag },
  { to: "/admin/blogs", label: "Blogs", icon: Article },
  { to: "/admin/faqs", label: "FAQs", icon: Question },
  { to: "/admin/inquiries", label: "Inquiries", icon: ChatCircleText },
  { to: "/admin/careers", label: "Careers", icon: Briefcase },
  { to: "/admin/applications", label: "Applications", icon: Files },
  { to: "/admin/clients", label: "Clients", icon: Buildings },
  { to: "/admin/testimonials", label: "Testimonials", icon: ChatCircleText },
  { to: "/admin/sliders", label: "Sliders", icon: ImageSquare },
  { to: "/admin/videos", label: "Videos", icon: VideoCamera },
  { to: "/admin/sections", label: "Page Content", icon: Stack },
  { to: "/admin/media", label: "Media Library", icon: ImageSquare },
  { to: "/admin/users", label: "Admin Users", icon: UsersThree },
  { to: "/admin/seo", label: "SEO", icon: MagnifyingGlass },
  { to: "/admin/redirects", label: "Redirects", icon: ArrowsLeftRight },
  { to: "/admin/settings", label: "Settings", icon: Gear },
];

export default function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-[#F7F8F6] flex" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
      {/* Sidebar */}
      <aside className="w-64 bg-[#F7F8F6] border-r border-[#E2E8F0] flex flex-col sticky top-0 h-screen" data-testid="admin-sidebar">
        <div className="px-6 py-6 border-b border-[#E2E8F0]">
          <Link to="/admin" className="block">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#4B5563] mb-1">Bonheur Pharma</div>
            <div className="text-xl font-bold text-[#C2410C]" style={{ fontFamily: "'Manrope', sans-serif" }}>
              Admin Console
            </div>
          </Link>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-0.5">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors duration-200 ${
                    isActive
                      ? "bg-[#C2410C]/10 text-[#C2410C] font-semibold"
                      : "text-[#4B5563] hover:bg-white hover:text-[#C2410C]"
                  }`
                }
              >
                <Icon size={18} weight="regular" />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>
        <div className="px-3 py-4 border-t border-[#E2E8F0]">
          <div className="flex items-center gap-3 px-2 py-2">
            <UserCircle size={28} weight="duotone" className="text-[#C2410C]" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-[#1F2937] truncate">{user?.name}</div>
              <div className="text-xs text-[#4B5563] truncate">{user?.email}</div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="admin-logout"
            className="mt-1 w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-[#4B5563] hover:bg-white hover:text-[#EF4444] transition-colors duration-200"
          >
            <SignOut size={16} />
            <span>Sign out</span>
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">{children}</main>
    </div>
  );
}

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="px-8 py-6 border-b border-[#E2E8F0] bg-white sticky top-0 z-30 flex items-end justify-between gap-6">
      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#4B5563] mb-1">Admin / {title}</div>
        <h1 className="text-2xl sm:text-3xl tracking-tight font-bold text-[#1F2937]" style={{ fontFamily: "'Manrope', sans-serif" }}>
          {title}
        </h1>
        {subtitle && <p className="text-sm text-[#4B5563] mt-1">{subtitle}</p>}
      </div>
      <div className="flex items-center gap-2">{actions}</div>
    </div>
  );
}
